import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@gradebook/ui';
import { useAuthStore } from '@/store/authStore';
import api from '@/lib/api';
import { ROUTES } from '@/lib/routes';
import type { LoginResponse, Membership } from '@/types/api';

/**
 * Moves the session between the schools one person belongs to.
 *
 * Renders nothing at all for the ordinary single-school account, which is almost
 * everyone - the switcher appears only once the API reports a second membership.
 *
 * Switching is a server operation, not a client one: the API mints a token for
 * the other membership (with that school's user id and that school's role) and
 * this adopts it. There is deliberately no way to change the active school
 * without a round trip, because the token is what the tenancy layer trusts.
 */
export function SchoolSwitcher() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const memberships = useAuthStore((state) => state.memberships);
  const setMemberships = useAuthStore((state) => state.setMemberships);
  const switchSchool = useAuthStore((state) => state.switchSchool);
  const isImpersonating = useAuthStore((state) => state.impersonation?.isImpersonating ?? false);

  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  // Asked once per session. A failure is silent on purpose: not knowing about a
  // second school costs the switcher, and an error banner over the whole app for
  // a feature most accounts never use would be worse than its absence.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get<{ memberships: Membership[] }>('/api/auth/memberships');
        if (!cancelled) setMemberships(response.data.memberships);
      } catch {
        /* no switcher this session */
      }
    })();
    return () => {
      cancelled = true;
    };
    // Re-asked when the active school changes, so the list reflects the session
    // that is actually in force.
  }, [user, setMemberships]);

  // The API refuses to switch while impersonating, so offering it would be
  // offering something that cannot work.
  if (!user || memberships.length <= 1 || isImpersonating) return null;

  const current = memberships.find((membership) => membership.schoolId === user.schoolId);

  const onSwitch = async (schoolId: string) => {
    if (schoolId === user.schoolId || busy) {
      setOpen(false);
      return;
    }

    setBusy(true);
    try {
      const response = await api.post<LoginResponse>('/api/auth/switch-school', { schoolId });
      const { token, user: nextUser } = response.data;

      // Everything cached belongs to the school being left. Clearing before the
      // navigate means the next screen fetches rather than painting the previous
      // school's roster under this school's name - the one visible failure this
      // feature invites.
      queryClient.clear();
      switchSchool(nextUser, token);
      setOpen(false);
      navigate(ROUTES.dashboard(nextUser.schoolSlug));
    } catch {
      // Most likely the membership was revoked between listing and clicking.
      // Re-read the list so the dead entry stops being offered.
      try {
        const refreshed = await api.get<{ memberships: Membership[] }>('/api/auth/memberships');
        setMemberships(refreshed.data.memberships);
      } catch {
        /* leave the list as it was */
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative px-3 py-2">
      <Button
        variant="secondary"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        disabled={busy}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex w-full items-center justify-between text-left"
      >
        <span className="min-w-0">
          <span className="block text-xs text-neutral-500">School</span>
          <span className="block truncate font-medium text-neutral-900">
            {current?.schoolName ?? user.schoolSlug}
          </span>
        </span>
        <svg
          className="h-4 w-4 shrink-0 text-neutral-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
        </svg>
      </Button>

      {open && (
        <ul
          role="listbox"
          aria-label="Switch school"
          className="absolute bottom-full left-3 right-3 z-20 mb-1 overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-lg"
        >
          {memberships.map((membership) => {
            const isCurrent = membership.schoolId === user.schoolId;
            return (
              <li key={membership.schoolId}>
                <Button
                  variant="ghost"
                  role="option"
                  aria-selected={isCurrent}
                  disabled={busy}
                  onClick={() => onSwitch(membership.schoolId)}
                  className="flex w-full items-center justify-between text-left"
                >
                  <span className="truncate">{membership.schoolName}</span>
                  {/* The roles differ per school, and are the thing that makes
                      two similarly-named schools tellable apart. Joined, because
                      React concatenates an array child with no separator - a
                      teacher who is also a parent here read as "Teacherparent". */}
                  <span className="ml-2 shrink-0 text-xs capitalize text-neutral-500">
                    {membership.roles.join(', ')}
                  </span>
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
